import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../Components/ui/card';
import { Mail, ArrowLeft } from 'lucide-react';

function ForgotPassword() {
    const [email, setEmail] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [messageType, setMessageType] = useState<'success' | 'error'>('success');
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        if (!email) {
            setMessage('Please enter your email address');
            setMessageType('error');
            return;
        }

        setIsLoading(true);
        setMessage('');

        try {
            const response = await fetch('/Users/forgot-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (response.ok) {
                setMessage(data.message || 'If the email exists, a password reset link has been sent.');
                setMessageType('success');
                setEmail(''); // Clear the form
            } else {
                setMessage(data.error || 'An error occurred. Please try again.');
                setMessageType('error');
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            setMessage('An error occurred. Please try again.');
            setMessageType('error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    const successMsgStyle = "bg-green-500 p-3 text-white text-center font-bold rounded mb-4";
    const errorMsgStyle = "bg-red-500 p-3 text-white text-center font-bold rounded mb-4";

    return (
        <div className="flex flex-row min-h-screen justify-center items-center">
            <Card className="flex flex-col justify-self-center p-8 w-full max-w-md">
                <h3 className="mb-6 text-3xl font-bold text-center">Forgot Password</h3>
                <p className="mb-6 text-center text-gray-600">
                    Enter your email address and we'll send you a link to reset your password.
                </p>
                
                <form className="w-full" onSubmit={handleSubmit}>
                    <div className="md:flex md:items-center mb-6">
                        <div className="flex flex-row w-full bg-gray-200 rounded">
                            <label className="text-gray-500 font-bold content-center ml-1" htmlFor="email">
                                <Mail />
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                placeholder="john.doe@example.com"
                                className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    {message && (
                        <p className={messageType === 'success' ? successMsgStyle : errorMsgStyle}>
                            {message}
                        </p>
                    )}

                    <div className="md:flex md:items-center">
                        <button 
                            className="mb-4 w-full button-default" 
                            type="submit"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Sending...' : 'Send Reset Link'}
                        </button>
                    </div>

                    <div className="md:flex md:items-center">
                        <button 
                            className="w-full flex items-center justify-center gap-2 shadow bg-gray-600 hover:bg-gray-400 focus:shadow-outline focus:outline-none text-white font-bold py-2 px-4 rounded"
                            type="button"
                            onClick={handleBackToLogin}
                            disabled={isLoading}
                        >
                            <ArrowLeft size={16} />
                            Back to Login
                        </button>
                    </div>
                </form>

                {isLoading && (
                    <div className="flex flex-row mt-4">
                        <div className="flex flex-col m-auto">
                            <span className="loader"></span>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}

export default ForgotPassword;
